import { useParams, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { INDUSTRIES_DETAIL, INDUSTRY_FINISHES, INDUSTRY_WHY, PRODUCT_CATEGORIES } from "@/lib/data";
import { FadeIn, SectionLabel } from "@/components/Shared";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowRight, ArrowUpRight, Check, Sparkles, Award, Workflow, Zap, Sprout, Cog, X, Image as ImageIcon } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

const WHY_ICONS = [Award, Sparkles, Cog, Sprout, Zap, Workflow];

const INDUSTRY_GALLERY_FOLDER_BY_SLUG = {
  cosmetics: "cosmetics",
  perfume: "perfume",
  jewelry: "jewelry",
  "food-sweets": "foods",
  chocolate: "chocolate",
  electronics: "electronics",
  ecommerce: "Ecommerce",
  "fashion-apparel": "apparel",
  toys: "toys",
  "corporate-gifting": "gifts",
};

const PRODUCT_GALLERY_ASSETS = require.context(
  "../assets/industry/products",
  true,
  /\.(png|jpe?g|webp)$/i
);

const PRODUCT_GALLERY_BY_FOLDER = PRODUCT_GALLERY_ASSETS.keys().reduce((acc, assetPath) => {
  if (assetPath.includes("premiumCards")) return acc;
  const [, folder = "", file = ""] = assetPath.match(/^\.\/(.*?)\/(.+)$/) || [];
  if (!folder || !file) return acc;
  if (!acc[folder]) acc[folder] = [];
  acc[folder].push({
    file,
    src: PRODUCT_GALLERY_ASSETS(assetPath),
  });
  return acc;
}, {});

Object.values(PRODUCT_GALLERY_BY_FOLDER).forEach((items) => {
  items.sort((a, b) => a.file.localeCompare(b.file, undefined, { numeric: true, sensitivity: "base" }));
});

const PLACEHOLDER_GALLERY_ITEMS = [
  { id: "placeholder-01", title: "Box style 01", image: null },
  { id: "placeholder-02", title: "Box style 02", image: null },
  { id: "placeholder-03", title: "Box style 03", image: null },
  { id: "placeholder-04", title: "Box style 04", image: null },
  { id: "placeholder-05", title: "Box style 05", image: null },
];

function toKebabCase(value) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

function getIndustryGalleryItems(industry) {
  const folder = INDUSTRY_GALLERY_FOLDER_BY_SLUG[industry.slug];
  const assets = (folder && PRODUCT_GALLERY_BY_FOLDER[folder]) || [];
  if (assets.length === 0) return PLACEHOLDER_GALLERY_ITEMS;

  return assets.map(({ file, src }, index) => {
    const baseName = file.replace(/\.[^.]+$/, "");
    const sanitizedFile = toKebabCase(baseName);
    const count = String(index + 1).padStart(2, "0");
    return {
      id: `${industry.slug}-${sanitizedFile || count}`,
      title: `${industry.name} packaging ${count}`,
      image: src,
      collectionLabel: `${industry.name} gallery`,
    };
  });
}

function BoxGalleryCard({ item, onOpen, testId }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      data-testid={testId}
      className="group relative shrink-0 w-[264px] md:w-[300px] lg:w-[312px] aspect-[4/5] rounded-3xl overflow-hidden neo-card border border-white/10 hover:border-brand/40 bg-ink-900/60 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:scale-[1.03] hover:shadow-[0_25px_60px_-15px_rgba(244,90,42,0.35)] focus:outline-none focus:border-brand/50 text-left cursor-pointer"
      aria-label={`Preview ${item.title}`}
    >
      {item.image ? (
        <div className="relative w-full h-full">
          <img
            src={item.image}
            alt={item.title}
            className="w-full h-full object-cover transition duration-700 group-hover:scale-105"
            loading="lazy"
          />
        </div>
      ) : (
        <div className="relative w-full h-full flex flex-col items-center justify-center gap-3 text-center px-5">
          <div className="w-14 h-14 rounded-2xl bg-brand/10 border border-brand/25 flex items-center justify-center">
            <ImageIcon className="w-6 h-6 text-brand" />
          </div>
          <p className="text-[11px] uppercase tracking-[0.28em] text-white/40">Box style</p>
          <p className="text-sm text-white/60 leading-snug">
            Placeholder image
            <br />
            <span className="text-white/35 text-xs">1200 x 1500 px</span>
          </p>
        </div>
      )}
    </button>
  );
}

// Lightbox modal — dim + blur backdrop, close on backdrop click / Escape / × button
function GalleryLightbox({ item, onClose }) {
  useEffect(() => {
    if (!item) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [item, onClose]);

  return (
    <AnimatePresence>
      {item && (
        <motion.div
          key="backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-6 md:p-10"
          onClick={onClose}
          data-testid="gallery-lightbox"
        >
          {/* Backdrop — blur only, no darkening */}
          <div className="absolute inset-0 backdrop-blur-md bg-white/5" />

          {/* Dialog */}
          <motion.div
            key="dialog"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: 10 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={`Preview ${item.title}`}
            className="relative w-full max-w-[448px] md:max-w-[496px] aspect-[4/5] rounded-[2rem] overflow-hidden neo-card border border-white/15 bg-ink-900/95 shadow-[0_30px_80px_rgba(0,0,0,0.35)]"
            data-testid="gallery-lightbox-dialog"
          >
            <button
              type="button"
              onClick={onClose}
              data-testid="gallery-lightbox-close"
              className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-brand text-white flex items-center justify-center border border-white/15 transition"
              aria-label="Close preview"
            >
              <X className="w-4 h-4" />
            </button>

            {item.image ? (
              <div className="relative w-full h-full">
                <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className="relative w-full h-full">
                <div className="absolute inset-0 bg-gradient-to-br from-white/[0.04] via-transparent to-brand/[0.08] pointer-events-none" />
                <div className="absolute -top-24 -right-24 w-[300px] h-[300px] rounded-full bg-brand/15 blur-[120px] pointer-events-none" />
                <div className="relative w-full h-full flex flex-col items-center justify-center gap-5 text-center px-8 md:px-14">
                  <div className="w-20 h-20 rounded-3xl bg-brand/15 border border-brand/30 flex items-center justify-center">
                    <ImageIcon className="w-8 h-8 text-brand" />
                  </div>
                  <p className="text-[11px] uppercase tracking-[0.3em] text-brand">Box style · placeholder</p>
                  <h3 className="font-display text-2xl md:text-3xl font-medium leading-tight">{item.title}</h3>
                  <p className="text-sm md:text-base text-white/60 leading-relaxed max-w-sm">
                    Replace this placeholder with your actual box photo.
                  </p>
                  <p className="text-xs text-white/40">Recommended: 1200 x 1500 px · 4:5 · JPG / WebP · under 350 KB</p>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function IndustryDetail() {
  const { slug } = useParams();
  const ind = INDUSTRIES_DETAIL.find((x) => x.slug === slug);
  const [lightboxItem, setLightboxItem] = useState(null);

  if (!ind) {
    return (
      <div className="pt-40 pb-32 text-center">
        <h1 className="font-display text-4xl">Industry not found</h1>
        <Link to="/industries" className="text-brand mt-6 inline-block">Back to industries</Link>
      </div>
    );
  }



  const galleryItems = getIndustryGalleryItems(ind);

  const row1Base = galleryItems.slice(0, 5);
  const row2Base = galleryItems.length >= 10 ? galleryItems.slice(5, 10) : row1Base;
  const row3Base = galleryItems.length >= 15 ? galleryItems.slice(10, 15) : row1Base;
  const duplicateRow = (items, row) =>
    ["a", "b", "c"].flatMap((cycle) =>
      items.map((item) => ({ ...item, renderId: `${row}-${cycle}-${item.id}` }))
    );

  let popularStyleOrder = 0;
  let whyCardOrder = 0;

  return (
    <div data-testid={`industry-detail-${ind.slug}`}>
      {/* 1. HERO */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-24 overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-brand/10 blur-[140px] -z-10" />
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <FadeIn className="lg:col-span-6">
            <Link to="/industries" className="text-xs uppercase tracking-[0.3em] text-brand inline-flex items-center gap-2 mb-6">
              ← All industries
            </Link>
            <SectionLabel>Industry · {ind.name}</SectionLabel>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-medium tracking-tighter mt-5 leading-[0.95]">{ind.name}</h1>
            <p className="mt-7 text-base md:text-lg text-white/70 leading-[1.85] max-w-xl">{ind.overview}</p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link to="/get-quote" data-testid={`industry-hero-quote-${ind.slug}`} className="inline-flex items-center gap-2 bg-brand text-white rounded-full px-7 py-4 text-sm font-medium hover:bg-brand-600 transition shadow-[0_10px_40px_rgba(244,90,42,0.4)]">
                Get Quote <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/contact" className="inline-flex items-center gap-2 border border-white/15 rounded-full px-7 py-4 text-sm hover:bg-white/5 transition">
                Talk to Expert
              </Link>
            </div>
          </FadeIn>

          <FadeIn delay={0.15} className="lg:col-span-6">
            <div className="relative h-[460px] md:h-[560px] rounded-[2rem] overflow-hidden neo-card glow-orange">
              <img src={ind.img} alt={ind.name} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-tr from-black/40 to-transparent" />
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 2. PACKAGING SOLUTIONS */}
      {galleryItems.length > 0 && (
        <section className="py-20 md:py-28 bg-ink-800/30 border-y border-white/5">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <FadeIn>
              <SectionLabel>For {ind.name}</SectionLabel>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight mt-4 max-w-3xl leading-[1.05]">Packaging Solutions</h2>
              <p className="mt-5 text-white/60 max-w-2xl leading-relaxed">Curated product categories proven for {ind.name.toLowerCase()} brands.</p>
            </FadeIn>

          </div>

          {/* Industry box gallery cards — marquee rows */}
          <div className="mt-12" data-testid={`industry-box-placeholders-${ind.slug}`}>
            <div className="max-w-[1400px] mx-auto px-6 md:px-10 mb-5 flex items-center justify-between">
              <p className="text-xs uppercase tracking-[0.3em] text-white/40">Box gallery</p>
              <p className="text-[10px] uppercase tracking-[0.25em] text-white/30">Click to preview</p>
            </div>

            <div
              className="relative overflow-x-clip py-8 md:py-10"
              style={{
                WebkitMaskImage:
                  "linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)",
                maskImage:
                  "linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)",
              }}
              data-testid={`industry-marquee-${ind.slug}`}
            >
              {/* Row 1 scrolling left */}
              <div
                className="flex gap-6 md:gap-8 whitespace-nowrap animate-marquee"
                data-testid={`industry-marquee-row-1-${ind.slug}`}
              >
                {duplicateRow(row1Base, "r1").map((it) => (
                  <BoxGalleryCard
                    key={it.renderId}
                    item={it}
                    onOpen={setLightboxItem}
                    testId={`box-gallery-${ind.slug}-${it.renderId}`}
                  />
                ))}
              </div>

              {/* Row 2 scrolling right */}
              <div
                className="mt-8 md:mt-10 flex gap-6 md:gap-8 whitespace-nowrap animate-marquee-rev"
                data-testid={`industry-marquee-row-2-${ind.slug}`}
              >
                {duplicateRow(row2Base, "r2").map((it) => (
                  <BoxGalleryCard
                    key={it.renderId}
                    item={it}
                    onOpen={setLightboxItem}
                    testId={`box-gallery-${ind.slug}-${it.renderId}`}
                  />
                ))}
              </div>

              {/* Row 3 scrolling left */}
              <div
                className="mt-8 md:mt-10 flex gap-6 md:gap-8 whitespace-nowrap animate-marquee"
                data-testid={`industry-marquee-row-3-${ind.slug}`}
              >
                {duplicateRow(row3Base, "r3").map((it) => (
                  <BoxGalleryCard
                    key={it.renderId}
                    item={it}
                    onOpen={setLightboxItem}
                    testId={`box-gallery-${ind.slug}-${it.renderId}`}
                  />
                ))}
              </div>
            </div>
          </div>


        </section>
      )}

      {/* 3. POPULAR PACKAGING STYLES */}
      {ind.popularStyles?.length > 0 && (
        <section className="py-20 md:py-28">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <FadeIn>
              <SectionLabel>Most ordered</SectionLabel>
              <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tight mt-4 max-w-2xl leading-[1.05]">Popular Packaging Styles</h2>
            </FadeIn>

            <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {ind.popularStyles.map((g) => {
                popularStyleOrder += 1;
                return (
                  <FadeIn key={g.group} delay={(popularStyleOrder - 1) * 0.05}>
                    <div className="neo-card rounded-3xl p-7 h-full">
                      <div className="flex items-center gap-3 mb-5">
                        <div className="w-9 h-9 rounded-full bg-brand/15 border border-brand/30 flex items-center justify-center text-brand font-display text-sm">{String(popularStyleOrder).padStart(2, "0")}</div>
                        <h3 className="font-display text-xl font-medium">{g.group}</h3>
                      </div>
                      <ul className="space-y-3">
                        {g.items.map((it) => (
                          <li key={it} className="text-sm text-white/75 flex items-center gap-2"><Check className="w-3.5 h-3.5 text-brand" />{it}</li>
                        ))}
                      </ul>
                    </div>
                  </FadeIn>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* 4. PREMIUM FINISHING OPTIONS */}
      <section className="py-20 md:py-28 bg-ink-800/30 border-y border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <FadeIn>
            <SectionLabel>Finishing</SectionLabel>
            <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tight mt-4 max-w-2xl leading-[1.05]">Premium Finishing Options</h2>
            <p className="mt-5 text-white/60 max-w-2xl leading-relaxed">Mix and match finishes to define your brand's tactile signature.</p>
          </FadeIn>

          <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {INDUSTRY_FINISHES.map((f, i) => (
              <FadeIn key={f.name} delay={(i % 5) * 0.04}>
                <div className="group neo-card rounded-2xl overflow-hidden h-full">
                  <div className="relative h-32 overflow-hidden">
                    <img src={f.img} alt={f.name} className="w-full h-full object-cover grayscale-[30%] group-hover:grayscale-0 group-hover:scale-110 transition duration-700" />
                  </div>
                  <div className="p-4">
                    <h3 className="font-display text-sm font-medium leading-tight">{f.name}</h3>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* 5. WHY PACFULLY */}
      <section className="py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <FadeIn>
            <SectionLabel>Why Pacfully</SectionLabel>
            <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tight mt-4 max-w-3xl leading-[1.05]">Engineered for {ind.name.toLowerCase()} brands.</h2>
          </FadeIn>

          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {INDUSTRY_WHY.map((w) => {
              const Icon = WHY_ICONS[whyCardOrder % WHY_ICONS.length];
              const delay = whyCardOrder * 0.04;
              whyCardOrder += 1;
              return (
                <FadeIn key={w.title} delay={delay}>
                  <div className="neo-card rounded-3xl p-7 h-full hover:border-brand/30 transition">
                    <div className="w-11 h-11 rounded-xl bg-brand/15 border border-brand/30 flex items-center justify-center mb-5">
                      <Icon className="w-5 h-5 text-brand" />
                    </div>
                    <h3 className="font-display text-xl font-medium">{w.title}</h3>
                    <p className="text-sm text-white/55 mt-3 leading-relaxed">{w.desc}</p>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* 6. GALLERY — removed (replaced by the marquee placeholders in Packaging Solutions above) */}

      {/* 7. FAQ */}
      {ind.faqs?.length > 0 && (
        <section className="py-20 md:py-28">
          <div className="max-w-[1100px] mx-auto px-6 md:px-10">
            <FadeIn>
              <SectionLabel>FAQ</SectionLabel>
              <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tight mt-4 leading-[1.05]">Questions specific to {ind.name.toLowerCase()}.</h2>
            </FadeIn>

            <Accordion type="single" collapsible className="mt-12" data-testid={`industry-faq-${ind.slug}`}>
              {ind.faqs.map((f) => {
                const faqId = `${f.q}-${f.a}`.toLowerCase().replace(/[^a-z0-9]+/g, "-");
                return (
                  <AccordionItem key={faqId} value={faqId} className="border-white/10">
                    <AccordionTrigger className="font-display text-lg md:text-xl text-left hover:no-underline hover:text-brand py-6">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-white/65 text-base leading-relaxed pb-6">{f.a}</AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </div>
        </section>
      )}

      {/* 8. CTA */}
      <section className="py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="relative neo-card rounded-[2.5rem] p-10 md:p-20 overflow-hidden">
            <div className="absolute -top-32 -right-20 w-[500px] h-[500px] rounded-full bg-brand/20 blur-[120px]" />
            <FadeIn>
              <SectionLabel>Ready when you are</SectionLabel>
              <h2 className="font-display text-4xl md:text-6xl lg:text-7xl font-medium tracking-tighter mt-5 max-w-3xl leading-[0.95]">
                Let's build premium packaging<br />for your {ind.name.toLowerCase()} brand.
              </h2>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/get-quote" data-testid={`industry-cta-${ind.slug}`} className="inline-flex items-center gap-2 bg-brand text-white rounded-full px-7 py-4 text-sm font-medium hover:bg-brand-600 transition">
                  Get Custom Quote <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/contact" className="inline-flex items-center gap-2 border border-white/15 rounded-full px-7 py-4 text-sm hover:bg-white/5 transition">
                  Talk to Expert
                </Link>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Gallery lightbox modal */}
      <GalleryLightbox item={lightboxItem} onClose={() => setLightboxItem(null)} />
    </div>
  );
}
